import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import StatusBadge from "@/components/base/StatusBadge";
import CredibilityMeter from "@/components/base/CredibilityMeter";
import TopicCard from "@/components/base/TopicCard";
import EvidenceList from "@/pages/topics/components/EvidenceList";
import Timeline from "@/pages/topics/components/Timeline";
import { topics } from "@/mocks/topics";
import { topicDetails } from "@/mocks/topicDetails";
import { usePageMeta } from "@/hooks/usePageMeta";

const anchors = [
  { id: "summary", label: "事件摘要" },
  { id: "evidence", label: "证据链" },
  { id: "timeline", label: "时间线" },
  { id: "verdict", label: "真假辨析" },
  { id: "related", label: "相关追踪" },
];

export default function TopicDetailPage() {
  const { id } = useParams();
  const topic = topics.find((t) => t.id === id);
  const detail = topicDetails.find((d) => d.id === id);

  usePageMeta({
    title: topic
      ? `${topic.title} - 证据链与可信度追踪 | 蝌蚪爆料`
      : "事件未找到 | 蝌蚪爆料",
    description: topic
      ? `${topic.summary.slice(0, 70)}…蝌蚪爆料提供该事件的证据链、时间线与真假辨析记录。`
      : "该追踪事件不存在或已下线，返回列表查看其他事件。",
    keywords: topic
      ? `${topic.title},蝌蚪爆料证据,蝌蚪爆料截图对比,蝌蚪爆料真假,可信度`
      : "蝌蚪爆料,爆料追踪",
    canonicalPath: topic ? `/topic/${topic.id}` : "/topics",
    jsonLd: topic
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: topic.title,
            description: topic.summary,
            datePublished: topic.date,
            dateModified: topic.updated,
            articleSection: topic.category,
            author: { "@type": "Organization", name: "蝌蚪爆料" },
            publisher: { "@type": "Organization", name: "蝌蚪爆料" },
            mainEntityOfPage: `https://jiajiazizz.cn/topic/${topic.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://jiajiazizz.cn/" },
              { "@type": "ListItem", position: 2, name: "爆料追踪", item: "https://jiajiazizz.cn/topics" },
              { "@type": "ListItem", position: 3, name: topic.title, item: `https://jiajiazizz.cn/topic/${topic.id}` },
            ],
          },
        ]
      : undefined,
  });

  if (!topic || !detail) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-[1280px] px-4 py-24 text-center md:px-6">
          <i className="ri-file-unknow-line text-4xl text-foreground-400" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">事件未找到</h1>
          <p className="mt-3 text-sm text-foreground-600">
            该追踪记录可能已下线或链接有误，回到列表看看其他事件。
          </p>
          <Link
            to="/topics"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            返回追踪列表 <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = topics.filter((t) => t.id !== topic.id && t.category === topic.category).slice(0, 2);
  const fallbackRelated = topics.filter((t) => t.id !== topic.id).slice(0, 2);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  return (
    <SiteLayout>
      <article>
        <div className="border-b border-background-300 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/topics" className="hover:text-primary-700 cursor-pointer">爆料追踪</Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to={`/topics?category=${encodeURIComponent(topic.category)}`}
                className="hover:text-primary-700 cursor-pointer"
              >
                {topic.category}
              </Link>
            </nav>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <StatusBadge status={topic.status} size="md" />
              <span className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 font-label text-sm text-foreground-700">
                {topic.category}
              </span>
            </div>

            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {topic.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line" /> 首发 {topic.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-refresh-line" /> 最近更新 {topic.updated}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-file-list-3-line" /> {topic.evidenceCount} 项依据
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-fire-line" /> 热度 {topic.heat}
              </span>
            </div>
          </div>
        </div>

        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="overflow-hidden rounded-lg border border-background-300">
              <img
                src={topic.cover}
                alt={`${topic.title} 相关示意图`}
                className="h-56 w-full object-cover md:h-80"
              />
            </div>

            <section id="summary" className="mt-10 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件摘要</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{detail.summary}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {topic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md border border-background-300 bg-background-100 px-3 py-1 font-label text-xs text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </section>

            <section id="evidence" className="mt-12 scroll-mt-24">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-stack-line" />
                </span>
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">证据链</h2>
                  <p className="font-label text-xs text-foreground-500">按强度排序，逐条标注来源</p>
                </div>
              </div>
              <div className="mt-6">
                <EvidenceList items={detail.evidence} />
              </div>
            </section>

            <section id="timeline" className="mt-12 scroll-mt-24">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-history-line" />
                </span>
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件时间线</h2>
                  <p className="font-label text-xs text-foreground-500">从首次出现到最近一次复核</p>
                </div>
              </div>
              <div className="mt-6 rounded-lg border border-background-300 bg-background-100 p-6">
                <Timeline items={detail.timeline} />
              </div>
            </section>

            <section id="verdict" className="mt-12 scroll-mt-24">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-scales-3-line" />
                </span>
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">真假辨析</h2>
                  <p className="font-label text-xs text-foreground-500">可验证结论与待确认部分分开呈现</p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-5">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-secondary-900">
                    <i className="ri-checkbox-circle-line" /> 可验证部分
                  </h3>
                  <ul className="mt-4 space-y-3">
                    {detail.credible.map((point) => (
                      <li key={point} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-secondary-500" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-primary-900">
                    <i className="ri-error-warning-line" /> 矛盾与疑问
                  </h3>
                  <ul className="mt-4 space-y-3">
                    {detail.disputed.map((point) => (
                      <li key={point} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-background-300 bg-foreground-950 p-6">
                <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-background-50">
                  <i className="ri-file-shield-2-line text-accent-300" /> 综合判断
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-background-200 md:text-base">
                  {detail.verdict}
                </p>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="rounded-lg border border-background-300 bg-background-100 p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-950">可信度评分</h2>
                <div className="mt-4">
                  <CredibilityMeter score={topic.credibility} />
                </div>
                <p className="mt-4 border-t border-background-200 pt-4 text-xs leading-relaxed text-foreground-600">
                  评分综合证据强度、来源可追溯性与交叉验证情况得出，仅代表当前可用依据的完整度。
                </p>
              </div>

              <nav className="rounded-lg border border-background-300 bg-background-100 p-5" aria-label="本页目录">
                <h2 className="font-heading text-sm font-semibold text-foreground-950">本页目录</h2>
                <ul className="mt-4 space-y-2.5">
                  {anchors.map((a) => (
                    <li key={a.id}>
                      <a
                        href={`#${a.id}`}
                        className="flex items-center gap-2 font-label text-sm text-foreground-600 transition-colors hover:text-primary-700 cursor-pointer"
                      >
                        <span className="h-px w-3 bg-background-400" />
                        {a.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="rounded-lg border border-dashed border-primary-300 bg-primary-50/60 p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-950">发现信息有误？</h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  如果你掌握新的原始素材或更正说明，可以通过反馈渠道提交。
                </p>
                <Link
                  to="/about"
                  className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-primary-400 px-3 py-2 font-label text-xs text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
                >
                  提交更正 <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <section id="related" className="scroll-mt-24 border-t border-background-300 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
            <div className="flex items-end justify-between gap-4">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关追踪</h2>
              <Link
                to="/topics"
                className="inline-flex items-center gap-1 whitespace-nowrap font-label text-sm text-primary-700 hover:text-primary-600 cursor-pointer"
              >
                全部事件 <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {relatedList.map((item) => (
                <TopicCard key={item.id} topic={item} variant="row" />
              ))}
            </div>
          </div>
        </section>
      </article>
    </SiteLayout>
  );
}